import React from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircleIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

const About: React.FC = () => {
  const achievements = [
    "Generated ₹5L+ in revenue for client businesses",
    "Delivered 3+ enterprise-grade applications serving 10,000+ users",
    "Achieved 100% average ROI across all completed projects",
    "Maintained 98% on-time project delivery rate",
    "Cross-industry expertise: AgriTech, Construction, E-Commerce, Healthcare",
    "Professional support with <2 hour average response time",
    "Client-centric approach with iterative revisions",
    "Complete confidentiality with NDA & IP protection"
  ];

  const interests = [
    "Enterprise Web Applications",
    "Cross-Platform Mobile Development",
    "AgriTech & Social Impact Solutions",
    "AI/ML Integration & Computer Vision",
    "Real-time Systems & IoT Solutions",
    "E-Commerce & Payment Solutions"
  ];

  return (
    <section id="about" className="section-padding hand-drawn-bg">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            About <span className="gradient-text">PragyaTek Solutions</span>
            <span className="sr-only"> - Professional Web Development Company in Dhanbad, Jharkhand</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            A unit of Chakravega Technologies Private Limited (CIN: U62099JH2026PTC028403). Leading web & app development agency in BIT Sindri, Dhanbad, Jharkhand specializing in web development, mobile applications, and AI solutions. Engineering Intelligence for a Safer and Smarter Future.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          viewport={{ once: true }}
          className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4 mb-14"
        >
          <a href="#projects" className="group rounded-3xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-950/90 p-6 shadow-xl hover:-translate-y-1 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-3xl bg-primary-100 text-primary-700">
                <span className="text-2xl">🌾</span>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-400">Achhadam</p>
                <h3 className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">AgriTech Platform</h3>
              </div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              Connected 10,000+ farmers, enabling ₹20L+ in verified agricultural transactions with real-time price discovery.
            </p>
            <div className="mt-5 text-sm font-semibold text-primary-600 group-hover:text-primary-700">View Achhadam →</div>
          </a>

          <a href="#projects" className="group rounded-3xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-950/90 p-6 shadow-xl hover:-translate-y-1 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-3xl bg-slate-100 text-slate-900 dark:bg-slate-900/80 dark:text-slate-200">
                <span className="text-2xl">🏗️</span>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-400">Ramsethu</p>
                <h3 className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">Construction Workflow</h3>
              </div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              Managed over ₹3.5Cr+ worth of construction project data, schedules and vendor coordination with enterprise reliability.
            </p>
            <div className="mt-5 text-sm font-semibold text-primary-600 group-hover:text-primary-700">Explore Ramsethu →</div>
          </a>

          <a href="#projects" className="group rounded-3xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-950/90 p-6 shadow-xl hover:-translate-y-1 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-3xl bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
                <span className="text-2xl">👥</span>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-400">ACEBITS</p>
                <h3 className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">Community Platform</h3>
              </div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              Delivered a professional alumni experience serving 500+ active users with 99.9% uptime and secure member engagement.
            </p>
            <div className="mt-5 text-sm font-semibold text-primary-600 group-hover:text-primary-700">See ACEBITS →</div>
          </a>

          <a href="#projects" className="group rounded-3xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-950/90 p-6 shadow-xl hover:-translate-y-1 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-3xl bg-cyan-100 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-200">
                <span className="text-2xl">🧩</span>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-400">Axiomrise</p>
                <h3 className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">Enterprise Launch</h3>
              </div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              Scaled complex business systems with polished UI, strong infrastructure and enterprise-ready delivery for construction clients.
            </p>
            <div className="mt-5 text-sm font-semibold text-primary-600 group-hover:text-primary-700">Review Axiomrise →</div>
          </a>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content - Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative flex items-center justify-center lg:justify-start"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-purple-500 rounded-2xl sm:rounded-3xl blur-3xl opacity-60 animate-pulse"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 via-purple-400 to-pink-400 rounded-2xl sm:rounded-3xl blur-2xl opacity-40"></div>
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 p-1">
                <div className="bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8">
                  {/* Professional Development Image */}
                  <div className="relative overflow-hidden rounded-xl mb-6">
                    <img
                      src="/Assets/pragyatek ui/hands-typing-laptop-top-view.jpg"
                      alt="Professional Development Work"
                      className="w-full h-48 sm:h-56 md:h-64 object-cover rounded-xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-white font-bold text-lg mb-1">Elite Development Agency</h3>
                      <p className="text-white/90 text-sm">Building scalable solutions for startups & enterprises</p>
                    </div>
                  </div>

                  {/* Tech Stack Icons */}
                  <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-6">
                    {['React', 'Node.js', 'MongoDB', 'TypeScript', 'AWS', 'Docker'].map((tech) => (
                      <div key={tech} className="px-2 py-1 sm:px-3 sm:py-2 md:px-4 md:py-2 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-md sm:rounded-lg text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 shadow-md">
                        {tech}
                      </div>
                    ))}
                  </div>

                  {/* Trust Badges */}
                  <div className="flex justify-center gap-3 sm:gap-4 md:gap-6 pt-2 sm:pt-4">
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl md:text-4xl mb-1 sm:mb-2">🏆</div>
                      <div className="text-xs font-medium text-gray-600 dark:text-gray-400">Top</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl md:text-4xl mb-1 sm:mb-2">⚡</div>
                      <div className="text-xs font-medium text-gray-600 dark:text-gray-400">Fast</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl md:text-4xl mb-1 sm:mb-2">🔒</div>
                      <div className="text-xs font-medium text-gray-600 dark:text-gray-400">NDA</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl md:text-4xl mb-1 sm:mb-2">💯</div>
                      <div className="text-xs font-medium text-gray-600 dark:text-gray-400">100%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                <strong className="text-gray-900 dark:text-white">PragyaTek Solutions</strong> is a premier software development company based in Dhanbad, Jharkhand, specializing in full-stack web development, mobile application development, and AI/ML solutions. With over 5 years of experience in the IT industry, we deliver high-quality, scalable software solutions that drive business growth and digital transformation.
              </p>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                Our expertise spans across modern web technologies including React.js, Node.js, MongoDB, and cloud platforms like AWS. We have successfully delivered numerous projects for clients across various industries, from AgriTech platforms serving thousands of users to enterprise-grade construction management systems.
              </p>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                We focus on delivering production-ready solutions with clean, maintainable code. Every project is built with security, scalability, and performance in mind. From initial consultation to deployment and maintenance, we ensure complete transparency and client satisfaction at every stage of the software development lifecycle.
              </p>
            </div>

            {/* Achievements */}
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <CheckCircleIcon className="w-6 h-6 text-primary-600" />
                Key Achievements
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {achievements.map((achievement, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-2 text-gray-600 dark:text-gray-400"
                  >
                    <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                    <span className="text-sm">{achievement}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Interests */}
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <HeartIcon className="w-6 h-6 text-primary-600" />
                Technical Expertise & Specializations
              </h3>
              <div className="flex flex-wrap gap-2">
                {interests.map((interest, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    viewport={{ once: true }}
                    className="px-3 py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium hover:scale-105 transition-transform cursor-default"
                  >
                    {interest}
                  </motion.span>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-6 pt-6">
              {[
                { icon: BriefcaseIcon, number: "₹5L+", label: "Client Revenue" },
                { icon: HeartIcon, number: "10K+", label: "App Users" },
                { icon: AcademicCapIcon, number: "100%", label: "Avg ROI" }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg"
                >
                  <stat.icon className="w-8 h-8 text-primary-600 mx-auto mb-2" />
                  <div className="text-lg font-bold text-gray-900 dark:text-white">
                    {stat.number}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About; 